import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Loader2, Trash2, ShoppingBag, CreditCard, Globe2, MailCheck,
  ShieldCheck, LogIn, UserPlus, Sparkles, ArrowRight, CheckCircle2,
  Wallet, Landmark, Building2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { api } from "../lib/api";
import { toast } from "sonner";

const loadRzp = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

/** Customer-facing simplified order status labels. */
export const CUSTOMER_STATUS_LABEL = {
  cart:              "Payment Pending",
  pending_approval:  "Payment Received · Admin Reviewing",
  moderation_hold:   "Admin Reviewing",
  paid:              "Payment Received · Admin Reviewing",
  approved:          "Admin Approved · Processing",
  processing:        "Processing",
  delivered:         "Processing",
  completed:         "Completed",
  cancelled:         "Cancelled",
  rejected:          "Rejected",
  fraud:             "On Hold",
  merged:            "Merged",
};

export default function Checkout() {
  const { cart, removeFromCart, clearCart, user } = useAuth();
  const { format, code: currentCode } = useCurrency();
  const nav = useNavigate();

  const [quote, setQuote] = useState(null);
  const [creating, setCreating] = useState(false);
  const [order, setOrder] = useState(null);

  // ── Email-verify controls (visible only for authed but unverified users) ──
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const emailVerified = Boolean(user?.email_verified) || Boolean(quote?.email_verified);

  // ── Payment gateway selection ──
  const [selectedGateway, setSelectedGateway] = useState(null); // "razorpay" | "payglocal"
  const [pgLoading, setPgLoading] = useState(false);

  // ── PayGlocal billing form (pre-filled from user.billing when available) ──
  const [pgCurrency, setPgCurrency] = useState(user?.preferred_currency || currentCode || "INR");
  const [billing, setBilling] = useState(() => ({
    first_name: user?.first_name || "",
    last_name:  user?.last_name  || "",
    address_street1: user?.billing?.address_line1 || "",
    address_street2: user?.billing?.address_line2 || "",
    address_city:    user?.billing?.city         || "",
    address_state:   user?.billing?.state        || "",
    address_postal_code: user?.billing?.postal_code || "",
    address_country: user?.billing?.country      || "IN",
    phone_number:    user?.phone || "",
  }));
  const setBill = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });

  // ── Live quote (auto-applies Optivize10 + shows 2% PG fee) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || cart.length === 0) { setQuote(null); return; }
      try {
        const items = cart.map(c => ({ sku: c.sku, quantity: c.quantity, domain: c.meta?.domain }));
        const { data } = await api.post("/orders/quote", { items });
        if (!cancelled) setQuote(data);
      } catch { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
  }, [user, JSON.stringify(cart.map(c => ({ sku: c.sku, q: c.quantity })))]); // eslint-disable-line

  const localFallback = useMemo(() => {
    const sub = cart.reduce((s, i) => s + i.price * i.quantity, 0);
    const pg = Math.round(sub * 0.02);
    return { subtotal_inr: sub, discount_inr: 0, gateway_fee_inr: pg, total_inr: sub + pg, coupon_applied: null };
  }, [cart]);

  const q = quote || localFallback;

  // ── Auth actions ──
  const sendVerifyOtp = async () => {
    setSendingOtp(true);
    try {
      const { data } = await api.post("/auth/send-email-otp", {});
      setOtpToken(data.otp_token);
      toast.success(`Verification email sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email");
    } finally { setSendingOtp(false); }
  };

  const verifyOtp = async () => {
    if (!otpToken || !otpCode) return;
    setVerifyingOtp(true);
    try {
      await api.post("/auth/verify-email-otp", { otp_token: otpToken, otp_code: otpCode.trim() });
      toast.success("Email verified — you can now pay");
      // Force a fresh quote so email_verified flips true instantly
      const items = cart.map(c => ({ sku: c.sku, quantity: c.quantity, domain: c.meta?.domain }));
      try {
        const { data } = await api.post("/orders/quote", { items });
        setQuote(data);
      } catch {}
      setOtpCode(""); setOtpToken(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code");
    } finally { setVerifyingOtp(false); }
  };

  const ensureOrder = async () => {
    if (order) return order;
    if (!emailVerified) { toast.error("Please verify your email first"); return null; }
    if (cart.length === 0) { toast.error("Cart is empty"); return null; }
    setCreating(true);
    try {
      const items = cart.map(c => ({ sku: c.sku, quantity: c.quantity, domain: c.meta?.domain }));
      const { data } = await api.post("/orders/checkout", {
        items, coupon: null, currency: "INR",
      });
      setOrder(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      return null;
    } finally { setCreating(false); }
  };

  // ── Razorpay hosted checkout (supports UPI, cards, netbanking, wallets, intl cards) ──
  const startRazorpay = async () => {
    const data = await ensureOrder();
    if (!data) return;
    const ok = await loadRzp();
    if (!ok) { toast.error("Razorpay SDK failed to load"); return; }
    new window.Razorpay({
      key: data.razorpay_key_id,
      amount: data.amount_smallest_unit,
      currency: data.currency,
      name: "Optivize Digital",
      description: `Order ${data.order_id.slice(0,8).toUpperCase()}`,
      order_id: data.razorpay_order_id,
      prefill: {
        name:    `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name,
        email:   user.email,
        contact: user.phone || "",
      },
      theme: { color: "#2563EB" },
      handler: async (response) => {
        try {
          await api.post("/orders/verify", {
            order_id: data.order_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Payment successful! Awaiting admin review.");
          clearCart();
          nav("/dashboard/orders");
        } catch { toast.error("Payment verification failed"); }
      },
      modal: { ondismiss: () => toast.info("Payment cancelled") },
    }).open();
  };

  // ── PayGlocal hosted checkout ──
  const startPayGlocal = async () => {
    const required = ["first_name","last_name","address_street1","address_city","address_state","address_postal_code","phone_number"];
    const missing = required.filter(k => !(billing[k] || "").trim());
    if (missing.length) { toast.error("Please fill all billing fields marked with *"); return; }
    const data = await ensureOrder();
    if (!data) return;
    setPgLoading(true);
    try {
      const { data: sess } = await api.post(`/payments/payglocal/session/${data.order_id}`, {
        currency: pgCurrency,
        billing_country: billing.address_country,
        billing: billing,
      });
      if (sess?.redirect_url) {
        toast.success("Redirecting to PayGlocal hosted checkout…");
        window.location.href = sess.redirect_url;
      } else { toast.error("PayGlocal did not return a redirect URL"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start PayGlocal session");
    } finally { setPgLoading(false); }
  };

  // ── Stage detection ──
  // Stage 1: not logged in       -> AuthGate
  // Stage 2: logged, not verified -> VerifyGate
  // Stage 3: verified            -> GatewaySelection (or GatewayForm if selected)
  const stage = !user ? "auth" : !emailVerified ? "verify" : "gateway";

  return (
    <div className="section max-w-6xl">
      <ProgressBar stage={stage} selectedGateway={selectedGateway}/>
      <h1 className="text-4xl font-black tracking-tighter text-white mt-6" style={{fontFamily:'Outfit'}}>Checkout</h1>
      <p className="text-gray-400 mt-1">Secure payment · Optivize Digital · No hidden GST — only a flat 2% Payment Gateway Fee.</p>

      <div className="grid md:grid-cols-3 gap-5 mt-8">
        {/* Left column — items + active stage */}
        <div className="md:col-span-2 space-y-4">
          <CartCard cart={cart} format={format} removeFromCart={removeFromCart}/>

          {cart.length === 0 ? null : stage === "auth" ? (
            <AuthGate/>
          ) : stage === "verify" ? (
            <VerifyGate
              user={user}
              sendVerifyOtp={sendVerifyOtp}
              sendingOtp={sendingOtp}
              otpToken={otpToken}
              otpCode={otpCode} setOtpCode={setOtpCode}
              verifyOtp={verifyOtp} verifyingOtp={verifyingOtp}
            />
          ) : !selectedGateway ? (
            <GatewaySelection onSelect={setSelectedGateway} currentCode={currentCode}/>
          ) : selectedGateway === "razorpay" ? (
            <RazorpayCard
              onPay={startRazorpay}
              loading={creating}
              back={()=>setSelectedGateway(null)}
              total={format(q.total_inr)}
            />
          ) : (
            <PayGlocalCard
              billing={billing}
              setBill={setBill}
              pgCurrency={pgCurrency} setPgCurrency={setPgCurrency}
              billingCountry={billing.address_country}
              setBillingCountry={(v)=>setBilling(b=>({...b, address_country: v}))}
              onPay={startPayGlocal}
              loading={creating || pgLoading}
              back={()=>setSelectedGateway(null)}
            />
          )}
        </div>

        {/* Right column — summary */}
        <SummaryCard q={q} format={format} currentCode={currentCode} emailVerified={emailVerified}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────  UI FRAGMENTS  ───────────────────────────── */

function ProgressBar({ stage, selectedGateway }) {
  const steps = [
    { key: "cart",    label: "Cart" },
    { key: "auth",    label: "Sign in" },
    { key: "verify",  label: "Verify email" },
    { key: "gateway", label: "Pay" },
  ];
  const order = ["cart","auth","verify","gateway"];
  // "current" step index
  const currentIdx = order.indexOf(stage);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="checkout-progress">
      {steps.map((s, i) => {
        const done = i < currentIdx || (i === currentIdx && stage === "gateway" && selectedGateway);
        const active = i === currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
              ${done ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                     : active ? "bg-blue-500/15 border-blue-500/50 text-blue-200"
                              : "bg-slate-800/40 border-slate-700 text-slate-500"}`}
              data-testid={`step-${s.key}`}>
              {done ? <CheckCircle2 size={12}/> : <span className={`h-4 w-4 inline-flex items-center justify-center rounded-full border ${active ? "border-blue-300" : "border-slate-600"}`}>{i+1}</span>}
              {s.label}
            </div>
            {i < steps.length - 1 && <div className={`h-px w-6 ${done ? "bg-emerald-400/50" : "bg-slate-700"}`}/>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CartCard({ cart, format, removeFromCart }) {
  return (
    <div className="card p-5">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <ShoppingBag size={16}/> Your Items ({cart.length})
      </h3>
      {cart.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Your cart is empty. <Link to="/pricing" className="text-cyan-400">Browse plans</Link>.
        </p>
      ) : (
        <ul className="divide-y divide-white/5" data-testid="cart-items">
          {cart.map(i => (
            <li key={i.sku + (i.meta?.domain || "")} className="py-3 flex items-center justify-between">
              <div>
                <div className="text-white">{i.name}</div>
                {i.meta?.domain && <div className="text-xs text-cyan-400">Domain: {i.meta.domain}</div>}
                <div className="text-xs text-gray-500">Qty {i.quantity}</div>
              </div>
              <div className="text-right flex items-center gap-3">
                <div className="text-white">{format(i.price * i.quantity)}</div>
                <button onClick={()=>removeFromCart(i.sku)} className="text-rose-400 hover:text-rose-300"
                  data-testid={`remove-${i.sku}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AuthGate() {
  return (
    <div className="card p-8 border border-blue-400/30 bg-blue-500/5" data-testid="auth-gate">
      <div className="text-center max-w-md mx-auto">
        <ShieldCheck className="mx-auto text-blue-300" size={44}/>
        <h3 className="text-white text-xl font-semibold mt-4" style={{fontFamily:'Outfit'}}>Sign in to continue</h3>
        <p className="text-slate-300 text-sm mt-2">
          Create an account or sign in — we need a verified email before accepting payment.
          New customers automatically get <strong className="text-emerald-300">10% off with Optivize10</strong>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Link to="/signup?next=/checkout" className="btn-primary inline-flex items-center gap-2 justify-center"
            data-testid="checkout-signup-btn">
            <UserPlus size={16}/> Create account
          </Link>
          <Link to="/login?next=/checkout" className="btn-ghost inline-flex items-center gap-2 justify-center"
            data-testid="checkout-login-btn">
            <LogIn size={16}/> I already have an account
          </Link>
        </div>
      </div>
    </div>
  );
}

function VerifyGate({ user, sendVerifyOtp, sendingOtp, otpToken, otpCode, setOtpCode, verifyOtp, verifyingOtp }) {
  return (
    <div className="card p-8 border border-amber-400/40 bg-amber-500/5" data-testid="verify-email-gate">
      <div className="text-center max-w-md mx-auto">
        <MailCheck className="mx-auto text-amber-300" size={44}/>
        <h3 className="text-white text-xl font-semibold mt-4" style={{fontFamily:'Outfit'}}>Verify your email</h3>
        <p className="text-slate-300 text-sm mt-2">
          For your security we need to confirm <strong className="text-white">{user?.email}</strong>.
          We&apos;ll email you both a <strong>1-tap link</strong> and a <strong>6-digit code</strong> — use whichever you like.
        </p>
        {!otpToken ? (
          <button onClick={sendVerifyOtp} disabled={sendingOtp}
            className="btn-primary mt-5 inline-flex items-center gap-2 justify-center"
            data-testid="send-verify-otp-btn">
            {sendingOtp ? <Loader2 size={14} className="animate-spin"/> : <MailCheck size={14}/>}
            Send verification email
          </button>
        ) : (
          <div className="mt-5 flex flex-col sm:flex-row items-center gap-2 justify-center">
            <input
              value={otpCode}
              onChange={(e)=>setOtpCode(e.target.value)}
              maxLength={10}
              inputMode="numeric"
              className="input w-40 text-center text-lg tracking-[8px] font-mono"
              placeholder="●●●●●●"
              data-testid="verify-otp-input"
            />
            <button onClick={verifyOtp} disabled={verifyingOtp || !otpCode}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="verify-otp-btn">
              {verifyingOtp ? <Loader2 size={14} className="animate-spin"/> : <ShieldCheck size={14}/>}
              Verify
            </button>
          </div>
        )}
        {otpToken && (
          <button onClick={sendVerifyOtp} disabled={sendingOtp}
            className="text-xs text-cyan-400 hover:text-cyan-300 underline mt-3 inline-block"
            data-testid="resend-verify-otp">
            Resend email
          </button>
        )}
      </div>
    </div>
  );
}

function GatewaySelection({ onSelect, currentCode }) {
  return (
    <div className="space-y-3" data-testid="gateway-selection">
      <h3 className="text-white text-lg font-semibold" style={{fontFamily:'Outfit'}}>Choose your payment method</h3>
      <p className="text-slate-400 text-sm">Both gateways are PCI-DSS certified. Your card details never touch our servers.</p>

      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <button
          onClick={()=>onSelect("razorpay")}
          className="card p-6 text-left border border-white/10 hover:border-blue-400/60 hover:bg-blue-500/5 transition-all group"
          data-testid="gateway-razorpay"
        >
          <div className="flex items-center justify-between">
            <div className="h-11 w-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <CreditCard className="text-blue-300" size={22}/>
            </div>
            <ArrowRight size={16} className="text-slate-500 group-hover:text-blue-300 transition-colors"/>
          </div>
          <h4 className="text-white font-bold text-lg mt-3" style={{fontFamily:'Outfit'}}>Razorpay</h4>
          <p className="text-slate-400 text-xs mt-1">India&apos;s trusted gateway. Best for INR + regional currencies.</p>
          <ul className="text-slate-300 text-xs mt-3 space-y-1.5">
            <MethodChip icon={<CreditCard size={11}/>} label="Domestic & International cards"/>
            <MethodChip icon={<Sparkles size={11}/>} label="UPI (Google Pay · PhonePe · Paytm)"/>
            <MethodChip icon={<Wallet size={11}/>} label="Wallets"/>
            <MethodChip icon={<Landmark size={11}/>} label="Netbanking"/>
          </ul>
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-300">
            Recommended for {currentCode === "INR" ? "you" : "INR payments"} <ArrowRight size={11}/>
          </span>
        </button>

        <button
          onClick={()=>onSelect("payglocal")}
          className="card p-6 text-left border border-white/10 hover:border-cyan-400/60 hover:bg-cyan-500/5 transition-all group"
          data-testid="gateway-payglocal"
        >
          <div className="flex items-center justify-between">
            <div className="h-11 w-11 rounded-xl bg-cyan-500/15 flex items-center justify-center">
              <Globe2 className="text-cyan-300" size={22}/>
            </div>
            <ArrowRight size={16} className="text-slate-500 group-hover:text-cyan-300 transition-colors"/>
          </div>
          <h4 className="text-white font-bold text-lg mt-3" style={{fontFamily:'Outfit'}}>PayGlocal</h4>
          <p className="text-slate-400 text-xs mt-1">Global card processing — INR, USD, GBP, EUR, CAD, AUD, SGD.</p>
          <ul className="text-slate-300 text-xs mt-3 space-y-1.5">
            <MethodChip icon={<CreditCard size={11}/>} label="Domestic Visa · Mastercard · Amex"/>
            <MethodChip icon={<Globe2 size={11}/>} label="International Visa · MC · Amex · Discover"/>
            <MethodChip icon={<Building2 size={11}/>} label="Multi-currency settlement"/>
          </ul>
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300">
            Recommended for {currentCode !== "INR" ? "you" : "international customers"} <ArrowRight size={11}/>
          </span>
        </button>
      </div>
    </div>
  );
}

function MethodChip({ icon, label }) {
  return (
    <li className="inline-flex items-center gap-1.5 text-slate-300">
      <span className="h-4 w-4 inline-flex items-center justify-center rounded bg-white/5 text-cyan-300">{icon}</span>
      {label}
    </li>
  );
}

function RazorpayCard({ onPay, loading, back, total }) {
  return (
    <div className="card p-6" data-testid="razorpay-card">
      <button onClick={back} className="text-xs text-slate-400 hover:text-slate-200 mb-3 inline-flex items-center gap-1"
        data-testid="gateway-back-btn">← Change payment method</button>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
          <CreditCard size={22} className="text-blue-300"/>
        </div>
        <div>
          <h4 className="text-white font-bold text-lg" style={{fontFamily:'Outfit'}}>Pay with Razorpay</h4>
          <p className="text-slate-400 text-xs">You&apos;ll be taken to Razorpay&apos;s hosted checkout to pay {total}.</p>
        </div>
      </div>
      <button onClick={onPay} disabled={loading}
        className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2 py-3"
        data-testid="pay-razorpay-btn">
        {loading ? <Loader2 size={18} className="animate-spin"/> : <CreditCard size={18}/>}
        Continue to Razorpay
      </button>
      <p className="text-[11px] text-slate-500 mt-3 text-center">
        Cards · UPI · Netbanking · Wallets · International cards
      </p>
    </div>
  );
}

function PayGlocalCard({ billing, setBill, pgCurrency, setPgCurrency, billingCountry, setBillingCountry, onPay, loading, back }) {
  const CURRENCIES = ["INR", "USD", "GBP", "EUR", "CAD", "AUD", "SGD"];
  return (
    <div className="card p-6" data-testid="payglocal-card">
      <button onClick={back} className="text-xs text-slate-400 hover:text-slate-200 mb-3 inline-flex items-center gap-1"
        data-testid="gateway-back-btn">← Change payment method</button>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-cyan-500/15 flex items-center justify-center">
          <Globe2 size={22} className="text-cyan-300"/>
        </div>
        <div>
          <h4 className="text-white font-bold text-lg" style={{fontFamily:'Outfit'}}>Pay with PayGlocal</h4>
          <p className="text-slate-400 text-xs">Confirm your billing details — required by international card networks.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="First name *" value={billing.first_name} onChange={setBill("first_name")} testid="pg-first-name"/>
        <F label="Last name *"  value={billing.last_name}  onChange={setBill("last_name")}  testid="pg-last-name"/>
        <F label="Address line 1 *" value={billing.address_street1} onChange={setBill("address_street1")} testid="pg-street1" colSpan/>
        <F label="Address line 2"   value={billing.address_street2} onChange={setBill("address_street2")} testid="pg-street2" colSpan/>
        <F label="City *"           value={billing.address_city}    onChange={setBill("address_city")}    testid="pg-city"/>
        <F label="State / Region *" value={billing.address_state}   onChange={setBill("address_state")}   testid="pg-state"/>
        <F label="Postal / ZIP *"   value={billing.address_postal_code} onChange={setBill("address_postal_code")} testid="pg-postal"/>
        <F label="Phone *"          value={billing.phone_number}    onChange={setBill("phone_number")}    testid="pg-phone"/>
        <div>
          <label className="label">Billing country *</label>
          <select value={billingCountry} onChange={(e)=>setBillingCountry(e.target.value)} className="input w-full" data-testid="pg-billing-country">
            {[
              ["IN","India"],["US","United States"],["GB","United Kingdom"],["CA","Canada"],
              ["AU","Australia"],["AE","UAE"],["SG","Singapore"],["DE","Germany"],["FR","France"],
              ["NL","Netherlands"],["IT","Italy"],["ES","Spain"],["IE","Ireland"],["JP","Japan"],
              ["NZ","New Zealand"],["ZA","South Africa"],["BR","Brazil"],["MX","Mexico"],
            ].map(([c,n])=><option key={c} value={c}>{n} ({c})</option>)}
          </select>
        </div>
        <div>
          <label className="label">Pay in *</label>
          <select value={pgCurrency} onChange={(e)=>setPgCurrency(e.target.value)} className="input w-full" data-testid="pg-currency">
            {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <button onClick={onPay} disabled={loading}
        className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 py-3"
        data-testid="pay-payglocal-btn">
        {loading ? <Loader2 size={18} className="animate-spin"/> : <Globe2 size={18}/>}
        Continue to PayGlocal
      </button>
      <p className="text-[11px] text-slate-500 mt-3 text-center">
        You&apos;ll be redirected to PayGlocal&apos;s secure payment page. Your card details never touch our servers.
      </p>
    </div>
  );
}

function F({ label, value, onChange, testid, colSpan = false }) {
  return (
    <div className={colSpan ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      <input value={value} onChange={onChange} className="input w-full" data-testid={testid}/>
    </div>
  );
}

function SummaryCard({ q, format, currentCode, emailVerified }) {
  return (
    <div className="card p-5 h-fit">
      <h3 className="text-white font-semibold mb-3">Summary</h3>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between text-gray-300"><span>Subtotal</span><span>{format(q.subtotal_inr)}</span></div>
        {q.discount_inr > 0 && (
          <div className="flex justify-between text-emerald-400" data-testid="summary-discount">
            <span>Discount{q.coupon_applied ? ` (${q.coupon_applied})` : ""}</span>
            <span>-{format(q.discount_inr)}</span>
          </div>
        )}
        <div className="flex justify-between text-gray-300" data-testid="summary-pg-fee">
          <span>Payment Gateway Fee (2%)</span><span>{format(q.gateway_fee_inr)}</span>
        </div>
        <div className="border-t border-white/10 my-2"/>
        <div className="flex justify-between text-white font-bold" data-testid="summary-total">
          <span>Total</span><span>{format(q.total_inr)}</span>
        </div>
        {q.coupon_applied && (
          <p className="text-[11px] text-emerald-300 mt-1" data-testid="new-customer-badge">
            ✨ New-customer discount applied automatically — welcome to Optivize!
          </p>
        )}
        {currentCode !== "INR" && (
          <div className="text-[11px] text-slate-500 mt-1">
            Displayed in <strong className="text-slate-300">{currentCode}</strong>.
            Charge currency is selected on the payment gateway page.
          </div>
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-white/5 space-y-1.5 text-[11px] text-slate-500">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 size={12} className={emailVerified ? "text-emerald-400" : "text-slate-600"}/>
          Email verified
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 size={12} className="text-slate-600"/>
          Admin will review your order after payment (typically &lt; 1 business day)
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-4">By paying you agree to our Terms &amp; Refund Policy. No hidden taxes — 2% PG fee only.</p>
    </div>
  );
}
